import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import './AgentInfo.css';

const getIsAdmin = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user'));
    return u?.roles?.includes('ROLE_ADMIN') || u?.roles?.includes('ROLE_ASSISTANT') || false;
  } catch { return false; }
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CountUp = ({ end, duration = 1600, prefix = '', suffix = '', start = false }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return undefined;
    if (prefersReducedMotion()) {
      setValue(end);
      return undefined;
    }
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * end));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, end, duration]);

  return (
    <span>
      {prefix}
      {value}
      {suffix}
    </span>
  );
};

const AgentInfo = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [bio, setBio] = useState('');
  const [bioLoading, setBioLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const isAdmin = getIsAdmin();

  useEffect(() => {
    fetch(`https://tophamtechbe.com/llm/v1/agent-knowledge/${SITE_CONFIG.SITE_ID}/about`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.about) setBio(data.about); })
      .catch(() => {})
      .finally(() => setBioLoading(false));
  }, []);

  const handleSave = async () => {
    setShowConfirm(false);
    const token = localStorage.getItem('jwt_token');
    try {
      const body = { about: editText };
      if (SITE_CONFIG.AMPLIFY_WEBHOOK) body.rebuild_webhook = SITE_CONFIG.AMPLIFY_WEBHOOK;
      const res = await fetch(`https://tophamtechbe.com/llm/v1/agent-knowledge/${SITE_CONFIG.SITE_ID}/about`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) { setBio(editText); setEditing(false); }
    } catch {}
  };

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = '';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const reduce = prefersReducedMotion();
    const revealEls = document.querySelectorAll('.agent-info-page .reveal');

    if (reduce) {
      revealEls.forEach((el) => el.classList.add('in-view'));
      setStatsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            if (entry.target === statsRef.current) setStatsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    revealEls.forEach((el) => observer.observe(el));
    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className={`agent-info-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <button className="agent-back-button" onClick={() => navigate('/')}>
        ← Home
      </button>

      <section className="agent-hero">
        <div className="agent-hero-inner">
          <div className="agent-photo reveal">
            <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/raw-s-hopkins.png" alt={SITE_CONFIG.AGENT.NAME} />
          </div>

          <div className="agent-details reveal">
            <span className="agent-eyebrow">Maricopa Real Estate</span>
            <h1>Sandy Hopkins</h1>
            <p className="agent-title">Luxury Residential Specialist</p>
            <p className="agent-location">Maricopa, Arizona</p>

            <div className="contact-info">
              <a className="contact-item" href="mailto:maricopasandy@gmail.com">
                <span className="contact-icon">✉</span>
                maricopasandy@gmail.com
              </a>
              <a className="contact-item" href="tel:(520) 213-3096">
                <span className="contact-icon">☎</span>
                (520) 213-3096
              </a>
              <div className="contact-item">
                <span className="contact-icon">⌂</span>
                License SA627284000 · Maricopa Real Estate Company
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="agent-stats reveal" ref={statsRef}>
        <div className="stat-item">
          <div className="stat-value"><CountUp end={177} start={statsVisible} /></div>
          <div className="stat-label">Homes Sold</div>
        </div>
        <div className="stat-item">
          <div className="stat-value"><CountUp end={18} start={statsVisible} /></div>
          <div className="stat-label">Years Experience</div>
        </div>
        <div className="stat-item">
          <div className="stat-value"><CountUp end={5} suffix=".0" start={statsVisible} /></div>
          <div className="stat-label">Star Rating</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">$70K–$655K</div>
          <div className="stat-label">Price Range</div>
        </div>
      </section>

      <section className="agent-bio-section reveal">
        <span className="agent-eyebrow">Get to know your agent</span>
        <h2>
          About Sandy
          {isAdmin && !editing && (
            <button className="bio-edit-btn" onClick={() => { setEditText(bio); setEditing(true); }}>✎</button>
          )}
        </h2>
        <div className="bio-content">
          {editing ? (
            <div className="bio-edit-area">
              <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={10} />
              <div className="bio-edit-actions">
                <button className="bio-save-btn" onClick={() => setShowConfirm(true)}>Save</button>
                <button className="bio-cancel-btn" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : bioLoading ? (
            <p>Loading...</p>
          ) : (
            bio && bio.split('\n').filter(l => l.trim()).map((line, i) => <p key={i}>{line}</p>)
          )}

          {showConfirm && (
            <div className="confirm-overlay">
              <div className="confirm-modal">
                <h3 className="confirm-title">Confirm Save</h3>
                <p className="confirm-message">Saving will trigger a site rebuild which takes about 3 minutes of downtime.</p>
                <div className="confirm-actions">
                  <button className="bio-save-btn" onClick={handleSave}>Yes, Save &amp; Rebuild</button>
                  <button className="bio-cancel-btn" onClick={() => setShowConfirm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div className="bio-block">
            <h3>Your Maricopa Luxury Real Estate Specialist</h3>
            <p>
              Sandy Hopkins is a dedicated real estate professional serving Maricopa, Ahwatukee, and Chandler, Arizona. With a focus on luxury residential properties, Sandy brings expertise and a client-first approach to every transaction.
            </p>
          </div>

          <div className="bio-block">
            <h3>Local Market Expertise</h3>
            <p>
              Specializing in the Maricopa and surrounding communities, Sandy has deep knowledge of the local market and helps buyers and sellers navigate the Arizona real estate landscape with confidence.
            </p>
          </div>

          <div className="bio-block">
            <h3>Committed to Excellence</h3>
            <p>
              Sandy is committed to providing exceptional service and leveraging modern marketing strategies to ensure her clients' properties receive maximum exposure and the best possible outcomes.
            </p>
          </div>

          <p className="closing-statement">
            For Sandy, real estate is about building lasting relationships and helping clients find their perfect home in the Arizona communities they love.
          </p>
        </div>
      </section>

      <section className="agent-cta-section reveal">
        <h2>Ready to Work Together?</h2>
        <p>Let's discuss your real estate goals and find the perfect home in Maricopa, Ahwatukee, or Chandler.</p>
        <div className="agent-cta-buttons">
          <a href="mailto:maricopasandy@gmail.com?subject=Real Estate Inquiry" className="cta-button primary">
            Email Me
          </a>
          <button onClick={() => { navigate('/'); setTimeout(() => { window.dispatchEvent(new CustomEvent('openGlobeMap')); }, 100); }} className="cta-button secondary">
            View Properties
          </button>
        </div>
      </section>
    </div>
  );
};

export default AgentInfo;
